import React, {useState, useEffect} from 'react';
import { PackageTrackingDetails, RequestChangeResponse, RequestChangePostResponse } from '../../../types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SelectorComponent from '../Shared/DropdownSelector/SelectorComponent';
import ActionIcon from '../Shared/Icons/ActionIcon';
import { InputRow, InputLabel, SelectorWrapper, SubmitButton, OptionContainer, SelectedOptionContainer, SpinnerContainer } from './RequestChangeInputs';
import toast from '../../../Scripts/App/displayToastMessage';
import moment from 'moment';

interface IntendedDateData extends RequestChangeResponse {
    IntendedDate: string;
    ValidIntendedDates: string[];
}

interface UpdateIntendedDateProps {
    packageDetails: PackageTrackingDetails;
}

const UpdateIntendedDateComponent = ({packageDetails}: UpdateIntendedDateProps) => {
    const [t] = useTranslation();
    const [intendedDateData, setIntendedDateData] = useState({IsLoaded: false, Data: {} as IntendedDateData});
    const [selectedDate, setSelectedDate] = useState<string>();
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string[]} | undefined>();

    const loadIntendedDateData = () => {
        const xhr = new XMLHttpRequest();
        const url = `Package/UpdateIntendedDateData` + 
                    `?jobId=${packageDetails.JobId}` + 
                    `&trackingNumber=${packageDetails.TrackingNumber}`;
        xhr.open('GET', url, true);
        xhr.onload = () => {
            const data = JSON.parse(xhr.responseText) as IntendedDateData;
            setIntendedDateData({IsLoaded: true, Data: data});
            if(data.Success && data.ValidIntendedDates && data.ValidIntendedDates.length > 0)
            {
                setSelectedDate(data.ValidIntendedDates[0]);
            }
            if(data.Message)
            {
                toast(data.Message, data.MessageType ?? 'success');
            }
        };
        xhr.send();
    }

    const reloadIntendedDateData = () => {
        setIntendedDateData({IsLoaded: false, Data: {} as IntendedDateData});
        loadIntendedDateData();
    }

    const sendIntendedDateRequest = () => {
        if(!selectedDate)
        {
            toast(t('update_intended_date_message').replace('{0}', packageDetails.DeliveryOptions.JobTypeName), 'error');
            return;
        }
        
        const xhr = new XMLHttpRequest();
        const url = `Package/UpdateIntendedDate`
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-type', 'application/json');
        const data = JSON.stringify({
            TrackingNumber: packageDetails.TrackingNumber,
            JobId: packageDetails.JobId,
            IsDelivery: packageDetails.DeliveryOptions.IsDelivery,
            IntendedDate: selectedDate
        });

        xhr.onload = () => {
            const response = JSON.parse(xhr.responseText) as RequestChangePostResponse;
            if(response.Success)
            {
                // refresh the page
                window.location.href = `Package/Details?trackingNumber=${packageDetails.TrackingNumber}&message=${t('job_updated_indended_date')}`;
            }
            else
            {
                setValidationErrors(response.ValidationErrors);
                if(response.Message)
                {
                    toast(response.Message, response.MessageType ?? 'success');
                }
            }
        }

        xhr.send(data);
    }

    useEffect(() => {
        loadIntendedDateData();
    }, []);

    const renderCurrentDate = (date) =>
        <SelectedOptionContainer>
            {moment(date).isValid() ? moment(date).toDate().toLocaleDateString() : ""}
        </SelectedOptionContainer>

    const renderSelectedDate = (date) =>
        <SelectedOptionContainer>
            {moment(date).toDate().toLocaleDateString()}
        </SelectedOptionContainer>

    const renderDate = (date) =>
        <OptionContainer key={date} onClick={() => setSelectedDate(date)}>
            {moment(date).toDate().toLocaleDateString()}
        </OptionContainer>


    if(!intendedDateData.IsLoaded) //Loading
        return <SpinnerContainer />;

    else if(!intendedDateData.Data.Success) //Load error
        return(
            <InputRow>
                <div>{t('request_change_load_error_message')}</div>
                <ActionIcon title={'refresh'} onClick={reloadIntendedDateData}>refresh</ActionIcon>
            </InputRow>
        );

    return(
        <>
            <InputRow>
                <InputLabel>{t('updated_intended_date_current')}</InputLabel>
                <SelectorWrapper>
                    {renderCurrentDate(intendedDateData.Data.IntendedDate)}
                </SelectorWrapper>
            </InputRow>
            <InputRow>
                <InputLabel>{t('update_intended_date_preferred_date')}</InputLabel>
                <SelectorWrapper>
                    <SelectorComponent 
                        label={t('request_change_no_options_available')}
                        selectedItem={selectedDate}
                        items={intendedDateData.Data.ValidIntendedDates}
                        renderSelectedItem={renderSelectedDate}
                        renderItems={(dates) => dates.map(renderDate)}
                        fitToSize={false}
                    />
                </SelectorWrapper>
            </InputRow>
            {selectedDate &&
                <SubmitButton handleClick={sendIntendedDateRequest} />}
        </>
    );
}

export default UpdateIntendedDateComponent;